import type { SecretInputValue } from '@/components/atoms/SecretInput';
import {
    type FieldValue,
    isSecretName,
    type ProviderAccount,
    type ProviderFieldSchema,
    type ProviderSchema,
    type SaveProviderAccountInput,
    type SecretName,
    secretNamesOf,
} from '@/domain/providerConfig';
import { toFormKey } from '@/routes/shared/ProviderConfig/form/formKeys';
import {
    normalizeValue,
    sameValue,
    secretsPatchOf,
} from '@/routes/shared/ProviderConfig/form/formLeaves';
import {
    type FormFieldValue,
    keepAllSecrets,
    toFormFieldValue,
} from '@/routes/shared/ProviderConfig/form/formValues';

export interface AccountFormValues {
    isActive: boolean;
    /** Keyed by `toFormKey`, since react-hook-form reads a dot as nesting. */
    fields: Record<string, FormFieldValue>;
    secrets: Record<SecretName, SecretInputValue>;
}

export type AccountFormPath = `fields.${string}` | `secrets.${SecretName}`;

export function accountSchemaOf(account: ProviderAccount): ProviderSchema {
    return {
        providerName: account.providerName,
        authType: account.authType,
        fields: account.schemaFields,
    };
}

export function accountValueFields(account: ProviderAccount): ProviderFieldSchema[] {
    return account.schemaFields.filter((field) => field.kind !== 'secret');
}

export function toAccountFormValues(account: ProviderAccount): AccountFormValues {
    const fields: Record<string, FormFieldValue> = {};
    for (const field of accountValueFields(account))
        fields[toFormKey(field.key)] = toFormFieldValue(field, account.fields[field.key] ?? null);
    return { isActive: account.isActive, fields, secrets: keepAllSecrets() };
}

/**
 * Only what the form changed: a value that reads as the stored one stays out, and so does
 * every secret left as it is. A create always says whether the new row is active.
 */
export function buildAccountSaveInput(
    account: ProviderAccount,
    values: AccountFormValues
): SaveProviderAccountInput {
    const fields: Record<string, FieldValue> = {};
    for (const field of accountValueFields(account)) {
        const value = values.fields[toFormKey(field.key)];
        if (!sameValue(value, account.fields[field.key] ?? null))
            fields[field.key] = normalizeValue(value);
    }
    const isCreate = account.rowId === null;
    return {
        expectedUpdatedAt: account.updatedAt,
        ...((isCreate || values.isActive !== account.isActive) && { isActive: values.isActive }),
        fields,
        secrets: secretsPatchOf(values.secrets, secretNamesOf(accountSchemaOf(account))),
    };
}

/** Whether the input would change anything; a create always would. */
export function hasAccountChanges(input: SaveProviderAccountInput): boolean {
    return (
        input.isActive !== undefined ||
        Object.keys(input.fields).length > 0 ||
        Object.keys(input.secrets).length > 0
    );
}

/** A server error path as the form names it, or null for one the form does not show. */
export function accountFormPath(serverPath: string): AccountFormPath | null {
    if (serverPath.startsWith('fields.'))
        return `fields.${toFormKey(serverPath.slice('fields.'.length))}`;
    if (serverPath.startsWith('secrets.')) {
        const name = serverPath.slice('secrets.'.length);
        return isSecretName(name) ? `secrets.${name}` : null;
    }
    return null;
}
